// ----------------------------------------------------------------------------
// Linked churches: the Worship Planner side of connecting to Church Hub. This
// issues a short-lived code, a Church Hub admin redeems it, and the request
// comes back here to approve. The two products keep separate church data;
// linking only records that both admins agreed. Same calls as the shared
// LinkedChurchesSection, drawn with the redesign's kit.
// ----------------------------------------------------------------------------

import { useCallback, useEffect, useState } from 'react';
import { hubLinkApi, type HubLinkStatus } from '@app/api/organization';
import { Banner, Button, Card } from '@app/components/ui';
import { logger } from '@app/utils/logger';

const EMPTY: HubLinkStatus = { status: 'None', hubChurchId: null, hubChurchName: null, requestedAt: null, hasActiveCode: false };

const STATUSES: HubLinkStatus['status'][] = ['None', 'Pending', 'Linked', 'Declined'];
/** Status matched case-insensitively; anything unknown is treated as not linked. */
const normalize = (l: HubLinkStatus): HubLinkStatus => ({
  ...EMPTY,
  ...l,
  status: STATUSES.find((s) => s.toLowerCase() === String(l?.status ?? '').toLowerCase()) ?? 'None',
});

export function LinkedChurches({ churchId }: { churchId: string }) {
  const [link, setLink] = useState<HubLinkStatus>(EMPTY);
  const [code, setCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    try {
      setLink(normalize(await hubLinkApi.get(churchId)));
    } catch (cause) {
      logger.warn('Could not read Church Hub link status', cause);
    }
  }, [churchId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const issueCode = async () => {
    setBusy(true);
    setError('');
    try {
      setCode((await hubLinkApi.generateCode(churchId)).code);
      await refresh();
    } catch {
      setError('Could not create a link code. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const decide = async (approved: boolean) => {
    setBusy(true);
    setError('');
    try {
      setLink(normalize(await hubLinkApi.decide(churchId, approved)));
      setCode(null);
    } catch {
      setError('Could not save that decision. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const hubName = link.hubChurchName || 'A Church Hub church';

  return (
    <Card as="section" className="flex flex-col gap-3.5" aria-labelledby="sec-linked">
      <div>
        <h2 id="sec-linked" className="text-[15px] font-semibold">
          Linked churches
        </h2>
        <p className="text-[13px] text-t4 mt-[3px] leading-normal">
          Connect this church to its Church Hub page so sermons, podcasts and posts published there can sit alongside your planning. Linking never shares
          your planner data, in either direction.
        </p>
      </div>

      {link.status === 'Pending' && (
        <div className="rounded-[16px] bg-amb-bg p-4">
          <p className="text-[14px] font-semibold">{hubName} wants to link to this church.</p>
          <p className="text-[13px] text-t2 mt-1">Approve it only if you recognise this name.</p>
          <div className="flex gap-2 mt-3">
            <Button variant="dark" onClick={() => void decide(true)} loading={busy}>
              Approve
            </Button>
            <Button variant="ghost" className="!bg-card" onClick={() => void decide(false)} disabled={busy}>
              Decline
            </Button>
          </div>
        </div>
      )}

      {link.status === 'Linked' && (
        <div className="rounded-[16px] bg-fill p-4">
          <p className="text-[14px] font-semibold">Linked to {link.hubChurchName || 'a Church Hub church'}</p>
          <p className="text-[13px] text-t4 mt-1">To disconnect, use Unlink from the Church Hub side.</p>
        </div>
      )}

      {(link.status === 'None' || link.status === 'Declined') && (
        <>
          {link.status === 'Declined' && <p className="text-[13px] text-t4">You declined the last request. A new code lets them try again.</p>}
          {code ? (
            <div className="rounded-[16px] bg-fill p-4">
              <p className="text-[13px] text-t3">Give this code to your Church Hub admin. It works once and expires in 48 hours.</p>
              <p className="font-mono text-[22px] font-bold tracking-[.12em] mt-2 select-all">{code}</p>
            </div>
          ) : (
            <Button variant="dark" className="self-start" onClick={() => void issueCode()} loading={busy}>
              Create link code
            </Button>
          )}
        </>
      )}

      {error && (
        <Banner tone="acc" role="alert">
          {error}
        </Banner>
      )}
    </Card>
  );
}
